/**
 * Submit a marketing form payload.
 *
 * Posts to the API server at `/api/marketing/leads`. In production
 * the marketing site is on `clauseark.com` and the API is on
 * `api.clauseark.com`; the fetch interceptor installed at app boot
 * (see `lib/apiBase.ts`) rewrites the same-origin `/api/...` path to
 * the configured `VITE_API_BASE_URL`. In dev (single-origin preview),
 * the relative path is proxied straight through to the api-server.
 *
 * The handler that ingests these submissions lives on the API
 * server side at `artifacts/api-server/src/routes/marketingLeads.ts`
 * and acknowledges the submission with a structured log entry. The
 * downstream HubSpot CRM forwarding is layered on by the separate
 * "Send marketing leads to HubSpot CRM" task; until that lands the
 * log entry is the system of record.
 *
 * Throws `SubmitFormError` on:
 * - network failure
 * - non-2xx HTTP status (including 400 validation errors from the server)
 * - unparseable / non-JSON response
 *
 * Callers MUST handle the error to surface inline failure UX to the user.
 */
export interface SubmitFormResult {
  ok: true;
  id: string;
}

export class SubmitFormError extends Error {
  readonly status: number;
  readonly fieldErrors?: Record<string, string>;
  constructor(
    message: string,
    options: { status?: number; fieldErrors?: Record<string, string> } = {},
  ) {
    super(message);
    this.name = "SubmitFormError";
    this.status = options.status ?? 0;
    this.fieldErrors = options.fieldErrors;
  }
}

interface ApiErrorBody {
  ok?: false;
  error?: string;
  fieldErrors?: Record<string, string>;
}

interface ApiSuccessBody {
  ok: true;
  id: string;
}

export async function submitForm(
  source: string,
  values: Record<string, unknown>,
): Promise<SubmitFormResult> {
  const payload = { ...values, source };
  // Root-relative `/api/...` so the apiBase fetch interceptor (see
  // `lib/apiBase.ts`) can rewrite to `https://api.clauseark.com/api/...`
  // in production builds. Do NOT prefix with `import.meta.env.BASE_URL`
  // — that would produce `/marketing/api/marketing/leads`, which neither
  // the api-server nor the interceptor's match rule recognises.
  const url = "/api/marketing/leads";

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw new SubmitFormError(
      err instanceof Error
        ? `Network error: ${err.message}`
        : "Network error reaching the submissions endpoint.",
    );
  }

  const contentType = res.headers.get("content-type") ?? "";
  let body: ApiSuccessBody | ApiErrorBody | null = null;
  if (contentType.includes("application/json")) {
    try {
      body = (await res.json()) as ApiSuccessBody | ApiErrorBody;
    } catch {
      throw new SubmitFormError("Server returned an invalid response.", {
        status: res.status,
      });
    }
  }

  if (!res.ok || !body || body.ok !== true) {
    const errBody = (body ?? {}) as ApiErrorBody;
    throw new SubmitFormError(
      errBody.error ?? `Request failed with status ${res.status}.`,
      { status: res.status, fieldErrors: errBody.fieldErrors },
    );
  }

  return { ok: true, id: body.id };
}
